<script>
    import {providerLocale} from '$lib/providerLocale.js';
    import {t} from '$utils/lang.js';
    import {waehrung} from '$utils/waehrung.js';
    import {loadScript} from '@paypal/paypal-js';
    import {onMount} from 'svelte';
    import { devLog as _devLog } from '$utils/devLog.js';
    const devLog = (tag, ...args) => _devLog(`PaypalGateway:${tag}`, ...args);

    let {
        paymentId,
        metadata,
        onApproved = () => {},
    } = $props();

    let isLoading = $state(true);
    let errorMessage = $state(null);
    let containerEl;

    // onMount (not $effect): the SDK must load exactly once. As an $effect it
    // re-ran whenever the reactive `metadata` prop changed (e.g. after a
    // fetchShippingAndPayment refetch), triggering a second loadScript that
    // tore down the in-flight render → "zoid destroyed all components".
    onMount(() => {
        let destroyed = false;
        let buttons;

        const init = async () => {
            try {
                const paypal = await loadScript({
                    clientId: metadata.clientId,
                    ...(metadata.merchantId ? {merchantId: metadata.merchantId} : {}),
                    currency: metadata.currency ?? waehrung(),
                    locale: metadata.locale ?? providerLocale(),
                    intent: metadata.intent ?? 'capture',
                    commit: metadata.commit ?? false,
                    components: metadata.components ?? 'buttons',
                    'disable-funding': metadata['disable-funding']
                        ?? 'sepa,bancontact,blik,eps,giropay,ideal,mercadopago,p24,venmo,card',
                });
                if (destroyed) return;

                isLoading = false;

                buttons = paypal.Buttons({
                    'enable-funding': 'paypal',
                    // disableMaxWidth lifts PayPal's 750px button cap (enforced
                    // inside their cross-origin iframe, unreachable via CSS) so
                    // the button fills the wider order card.
                    style: {height: 42, label: 'buynow', disableMaxWidth: true},
                    userAction: 'PAY_NOW',

                    createOrder: async () => {
                        errorMessage = null;
                        devLog('createOrder:start', {paymentId});
                        const params = new URLSearchParams({
                            cl: 'm7-checkout-dpc',
                            fnc: 'createGatewayOrder',
                            payment_id: paymentId,
                        });
                        const res = await fetch(`/index.php?${params}`, {
                            method: 'POST',
                            headers: {'Content-Type': 'application/json'},
                        });
                        if (!res.ok) throw new Error('Netzwerkfehler');
                        const data = await res.json();
                        if (!data?.order?.id) {
                            devLog('createOrder:error', {message: data?.message, data});
                            throw new Error(data?.message ?? t('Bestellung konnte nicht erstellt werden.'));
                        }
                        devLog('createOrder:done', {orderId: data.order.id});
                        return data.order.id;
                    },

                    onApprove: async (data) => {
                        devLog('onApprove', {orderID: data?.orderID});
                        onApproved();
                    },

                    onError: (err) => {
                        errorMessage = t('PayPal-Fehler. Bitte versuche es erneut.');
                        console.error('PayPal onError:', err);
                    },
                });
                await buttons.render(containerEl);
            } catch (err) {
                // Render gets aborted with "zoid destroyed all components" when
                // the component unmounts mid-load — that's not a user-facing error.
                if (destroyed) return;
                isLoading = false;
                errorMessage = t('PayPal konnte nicht geladen werden. Bitte Seite neu laden.');
                console.error('PayPal SDK load error:', err);
            }
        };

        init();

        return () => {
            destroyed = true;
            try { buttons?.close?.(); } catch { /* noop */ }
        };
    });
</script>

<div class="mt-3">
    <div bind:this={containerEl}>
        {#if isLoading}
            <div class="h-[42px] bg-gray-100 animate-pulse rounded"></div>
        {/if}
    </div>
    {#if errorMessage}
        <p class="text-scarlet text-sm mt-2">{errorMessage}</p>
    {/if}
</div>
